import {forwardRef, Ref} from 'react';
import './switch.scss';
import {
  FormControl,
  Stack,
  styled,
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
  SxProps,
} from '@mui/material';
import {Pallete} from '../../theme/bluebird';
import {Tooltip} from '../tooltip/tooltip';

const StyledSwitch = styled((props: MuiSwitchProps) => (
  <MuiSwitch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({theme}) => ({
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 20 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : Pallete.gray.light,
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

interface SwitchInputProps extends MuiSwitchProps {
  label?: string;
  errors?: string;
  disabledTooltip?: string;
}

export const Switch = forwardRef(
  (
    {label, errors, required, sx, disabled, disabledTooltip, ...rest}: SwitchInputProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const switchStyle: SxProps = {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '14px',
      '& .MuiSwitch-track': {},
      '& label': {},
    };

    if (disabled) {
      switchStyle['& .MuiSwitch-track'] = {
        backgroundColor: (Boolean(rest.value) ? Pallete.gray.general : '') + ' !important',
      };
      switchStyle['& label'] = {
        color: Pallete.gray.light + ' !important',
      };
    }

    return (
      <FormControl className="switch-input" sx={sx}>
        <Stack sx={switchStyle}>
          {label && <label htmlFor={rest.id}>{label}</label>}
          <Tooltip.NotAllowedIf condition={disabled} title={disabledTooltip}>
            <StyledSwitch
              inputRef={ref}
              disabled={disabled}
              checked={Boolean(rest.value)}
              {...rest}
            />
          </Tooltip.NotAllowedIf>
        </Stack>
      </FormControl>
    );
  }
);

export default Switch;
